import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import { Container } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import CustomInput from "../../../components/AuthenticationComponents/CustomInput";
import SecondaryButton from "../../../components/Common/SecondaryButton";

//Import API
import endpoints from "../../../api/endpoints";
import { get, post, patch } from "../../../api/axios";

//Utils
import { extractErrorMessage } from "../../../utils/errorHandler";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

import passwordIcon from "../../../assets/images/support/password-icon.svg";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";
import cancelIcon from "../../../assets/images/user-dashboard/remove-circle.svg";

/* -------------------------------------------------------------------------
 * AdminSettingsPage — editable admin profile fields, notification toggles, and
 * linked-account management.
 * ---------------------------------------------------------------------- */

// Validation schema using Yup
const profileSchema = Yup.object().shape({
  first_name: Yup.string()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters"),
  last_name: Yup.string()
    .required("Last name is required")
    .min(2, "Last name must be at least 2 characters"),
});

const platformSettingsSchema = Yup.object().shape({
  default_platform_fee_percent: Yup.number()
    .required("Default platform fee percent is required")
    .min(0, "Must be at least 0")
    .max(100, "Must be at most 100"),
  points_per_dollar_spent: Yup.number()
    .required("Points per dollar spent is required")
    .min(0, "Must be at least 0"),
  points_per_dollar_redeemed: Yup.number()
    .required("Points per dollar redeemed is required")
    .min(0, "Must be at least 0"),
});

const AdminSettingsPage = ({
  profile,
  onProfileFieldChange,
  onEditPhoto,
  onDeletePhoto,
  onChangePassword,
  onSaveChanges,
}) => {
  document.title = "Platform Settings | Omaha Food Lovers";
  const navigate = useNavigate();

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [profileInitialValues, setProfileInitialValues] = useState({
    first_name: "",
    last_name: "",
  });
  const [settingsInitialValues, setSettingsInitialValues] = useState({
    default_platform_fee_percent: 0,
    points_per_dollar_spent: 0,
    points_per_dollar_redeemed: 0,
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Fetch platform settings from API
  const fetchProfile = useCallback(async () => {
    try {
      // Fetch admin profile data
      const profileResponse = await get(endpoints.adminProfile);
      
      console.log("Profile Response:", profileResponse);
      
      if (profileResponse.data.success) {
        const profileData = profileResponse.data.data;
        
        console.log("Profile Data:", profileData);
        
        setProfileInitialValues({
          first_name: profileData.first_name || "",
          last_name: profileData.last_name || "",
        });
      } else {
        console.warn("Profile response success is false:", profileResponse.data);
      }

      // Fetch reward economics data
      const rewardResponse = await get(endpoints.adminRewardEconomics);
      
      if (rewardResponse.data.status) {
        const rewardData = rewardResponse.data.data;
        
        setSettingsInitialValues({
          default_platform_fee_percent: rewardData.default_platform_fee_percent || 0,
          points_per_dollar_spent: rewardData.points_per_dollar_spent || 0,
          points_per_dollar_redeemed: rewardData.points_per_dollar_redeemed || 0,
        });
      }

      // Fetch account security data for email
      const securityResponse = await get(endpoints.platformSettingsAccountSecurity);
      
      if (securityResponse.data.status) {
        const securityData = securityResponse.data.data;
        
        setEmail(securityData.email || "");
      }
    } catch (err) {
      console.error("Failed to fetch platform settings:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    }
  }, []);

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Removed automatic fetch on mount - only call when user clicks Link Account button

  // Handle profile form submission
  const handleProfileSubmit = async (values, { setSubmitting }) => {
    setSavingProfile(true);
    try {
      const profilePayload = {
        first_name: values.first_name,
        last_name: values.last_name,
      };

      const profileResponse = await patch(endpoints.adminProfile, profilePayload);

      if (profileResponse.data.success) {
        toastr.success("Profile updated successfully!");
        navigate("/admin/profile")
      }
    } catch (err) {
      console.error("Failed to update profile:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    } finally {
      setSavingProfile(false);
      setSubmitting(false);
    }
  };

  // Handle platform settings form submission
  const handleSettingsSubmit = async (values, { setSubmitting }) => {
    setSavingSettings(true);
    try {
      const settingsPayload = {
        default_platform_fee_percent: parseFloat(values.default_platform_fee_percent),
        points_per_dollar_spent: parseInt(values.points_per_dollar_spent),
        points_per_dollar_redeemed: parseInt(values.points_per_dollar_redeemed),
      };

      const response = await post(endpoints.adminRewardEconomics, settingsPayload);

      if (response.data.status) {
        toastr.success("Platform settings updated successfully!");
        fetchProfile();
      }
    } catch (err) {
      console.error("Failed to update platform settings:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    } finally {
      setSavingSettings(false);
      setSubmitting(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    // Validation
    if (!email || !currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirm password do not match");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return;
    }

    setChangingPassword(true);
    setPasswordError("");

    try {
      const response = await post(endpoints.platformSettingsAccountSecurity, {
        email,
        current_password: currentPassword,
        new_password: newPassword,
      });

      if (response.data.status) {
        const successMessage = response.data.message || "Your password has been changed successfully!";
        toastr.success(successMessage);
        setShowPasswordForm(false);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setPasswordError(response.data.message || "Failed to change password");
        toastr.error(response.data.message || "Failed to change password");
      }
    } catch (err) {
      console.error("Failed to change password:", err);
      const errorMessage = extractErrorMessage(err);
      setPasswordError(errorMessage);
      toastr.error(errorMessage);
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="page-content settings-page">
    <Container fluid>
      {/* Profile & Credentials Section */}
      <div className="surface-card">
        <div className="profile-header">
          <div>
            <h2 className="profile-title">Profile & Credentials</h2>
            <p className="rp-card__subtitle" style={{ margin: 0 }}>Manage your profile information and account credentials.</p>
          </div>
        </div>
        <Formik
          initialValues={profileInitialValues}
          validationSchema={profileSchema}
          onSubmit={handleProfileSubmit}
          enableReinitialize={true}
        >
            {({ values, errors, touched, handleChange, isSubmitting }) => (
              <Form>
                <div className="profile-layout">
                  <div className="fields-grid profile-fields admin-settings-grid">
                    {!showPasswordForm ? (
                      <>
                    <div>
                      <CustomInput
                        label="First Name"
                        name="first_name"
                        type="text"
                        value={values.first_name}
                        placeholder="Enter first name"
                        onChange={handleChange}
                        className="white-bg"
                      />
                      {errors.first_name && touched.first_name && <p className="field-error">{errors.first_name}</p>}
                    </div>
                    <div>
                      <CustomInput
                        label="Last Name"
                        name="last_name"
                        type="text"
                        value={values.last_name}
                        placeholder="Enter last name"
                        onChange={handleChange}
                        className="white-bg"
                      />
                      {errors.last_name && touched.last_name && <p className="field-error">{errors.last_name}</p>}
                    </div>
                    <div>
                      <CustomInput
                        label="Email"
                        name="email"
                        value={email}
                        onChange={() => {}}
                        className="white-bg"
                        disabled
                      />
                    </div>
                      </>
                    ) : (
                      <>
                    <div>
                      <CustomInput
                        label="Email"
                        name="email"
                        value={email}
                        onChange={() => {}}
                        className="white-bg"
                        disabled
                      />
                    </div>
                    <div>
                      <CustomInput
                        label="Current Password"
                        name="currentPassword"
                        type="password"
                        placeholder="Enter current password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="white-bg"
                      />
                    </div>
                    <div>
                      <CustomInput
                        label="New Password"
                        name="newPassword"
                        type="password"
                        placeholder="Enter new password (min 8 characters)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="white-bg"
                      />
                    </div>
                    <div>
                      <CustomInput
                        label="Confirm Password"
                        name="confirmPassword"
                        type="password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="white-bg"
                      />
                    </div>
                      </>
                    )}
                    {showPasswordForm && passwordError && (
                      <p className="field-error field-error--full-width">{passwordError}</p>
                    )}
                  </div>
                </div>

                <div className="form-actions">
                  <SecondaryButton
                    label={showPasswordForm ? "Cancel" : "Change Password"}
                    variant={showPasswordForm ? "light-grey" : "light-orange"}
                    icon={showPasswordForm ? cancelIcon : passwordIcon}
                    onClick={() => {
                      setShowPasswordForm(!showPasswordForm);
                      setPasswordError("");
                      setCurrentPassword("");
                      setNewPassword("");
                      setConfirmPassword("");
                    }}
                  />
                  {showPasswordForm ? (
                    <SecondaryButton
                      label="Save Password"
                      variant="light-orange"
                      icon={passwordIcon}
                      onClick={handlePasswordChange}
                      disabled={changingPassword}
                      loading={changingPassword}
                    />
                  ) : (
                    <SecondaryButton label="Save Profile" variant="orange" icon={tickIcon} type="submit" disabled={savingProfile || isSubmitting} loading={savingProfile || isSubmitting} />
                  )}
                </div>
              </Form>
            )}
          </Formik>
      </div>

      {/* Platform Settings Section */}
      <div className="surface-card" style={{ marginTop: "20px" }}>
        <div className="profile-header">
          <div>
            <h2 className="profile-title">Platform Settings</h2>
            <p className="rp-card__subtitle" style={{ margin: 0 }}>Configure platform-wide settings and reward economics.</p>
          </div>
        </div>
        <Formik
          initialValues={settingsInitialValues}
          validationSchema={platformSettingsSchema}
          onSubmit={handleSettingsSubmit}
          enableReinitialize={true}
        >
            {({ values, errors, touched, handleChange, isSubmitting }) => (
              <Form>
                <div className="profile-layout">
                  <div className="fields-grid profile-fields admin-settings-grid">
                    <div>
                      <CustomInput
                        label="Default Platform Fee Percent"
                        name="default_platform_fee_percent"
                        type="number"
                        value={values.default_platform_fee_percent}
                        placeholder="e.g. 3"
                        onChange={handleChange}
                        className="white-bg"
                      />
                      {errors.default_platform_fee_percent && touched.default_platform_fee_percent && <p className="field-error">{errors.default_platform_fee_percent}</p>}
                    </div>
                    <div>
                      <CustomInput
                        label="Points Per Dollar Spent"
                        name="points_per_dollar_spent"
                        type="number"
                        value={values.points_per_dollar_spent}
                        placeholder="e.g. 5"
                        onChange={handleChange}
                        className="white-bg"
                      />
                      {errors.points_per_dollar_spent && touched.points_per_dollar_spent && <p className="field-error">{errors.points_per_dollar_spent}</p>}
                    </div>
                    <div>
                      <CustomInput
                        label="Points Per Dollar Redeemed"
                        name="points_per_dollar_redeemed"
                        type="number"
                        value={values.points_per_dollar_redeemed}
                        placeholder="e.g. 100"
                        onChange={handleChange}
                        className="white-bg"
                      />
                      {errors.points_per_dollar_redeemed && touched.points_per_dollar_redeemed && <p className="field-error">{errors.points_per_dollar_redeemed}</p>}
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <SecondaryButton label="Save Settings" variant="orange" icon={tickIcon} type="submit" disabled={savingSettings || isSubmitting} loading={savingSettings || isSubmitting} />
                </div>
              </Form>
            )}
          </Formik>
      </div>
    </Container>
  </div>
  );
};

AdminSettingsPage.propTypes = {
  profile: PropTypes.shape({
    photoUrl: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    zipCode: PropTypes.string,
    address: PropTypes.string,
  }).isRequired,
  onProfileFieldChange: PropTypes.func,
  onEditPhoto: PropTypes.func,
  onDeletePhoto: PropTypes.func,
  onChangePassword: PropTypes.func,
  onSaveChanges: PropTypes.func,
};

AdminSettingsPage.defaultProps = {
  onProfileFieldChange: () => {},
  onEditPhoto: () => {},
  onDeletePhoto: () => {},
  onChangePassword: () => {},
  onSaveChanges: () => {},
  profile: {
    photoUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=200&q=80",
    firstName: "Alex",
    lastName: "Johnson",
    email: "alex.johnson@example.com",
    phone: "+1 (555) 123-4567",
    zipCode: "68102",
    address: "123 Main Street, Omaha, NE",
  },
};

export default AdminSettingsPage;
